import { useState, useMemo, memo, useEffect } from "react";
import { logger } from "@/utils/logger";
import { useNavigate, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useStableAuth } from "@/hooks/useStableAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { useDashboardData } from "@/hooks/useDashboardData";
import { UserAvatar } from "@/components/ui/user-avatar";
import { 
  Home, 
  Settings, 
  FileText, 
  Package, 
  TrendingUp,
  ChevronDown,
  ArrowUpDown,
  ClipboardList,
  Users,
  UserCheck,
  Building2,
  Boxes,
  CreditCard,
  LogOut,
  AlertTriangle,
  ShoppingCart,
  ShoppingBag,
  Truck,
  BarChart3,
  DollarSign,
  Wrench,
  CheckSquare,
  Fuel,
  ArrowDownToLine,
  History,
  BotMessageSquare,
  Calendar,
  Route,
  Inbox,
  MapPin,
  Wallet,
  Monitor,
  Factory,
  ArrowRightLeft,
  FolderKanban,
} from "lucide-react";
import { useNotificationsContext } from "@/contexts/NotificationsContext";
import { useTaskActivities } from "@/hooks/useTaskActivities";
import { useModuleAccess } from "@/hooks/useModuleAccess";
import { useSupplierTrips } from "@/hooks/useSupplierTrips";

interface SidebarProps {
  // UI-HEADER-FIXO ajuste (I.160): o botão de recolher fica na barra superior; o estado vem da página
  collapsed?: boolean;
}

interface SubmenuItem {
  id: string;
  label: string;
  icon: any;
  visible: boolean;
  badge?: string | null;
  submenu?: SubmenuItem[];
}

const SidebarComponent = ({ collapsed = false }: SidebarProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [expandedMenus, setExpandedMenus] = useState<string[]>(() => {
    const path = location.pathname.split('/').filter(Boolean);
    const view = path[1] || 'home';
    const currentView = view === 'home' ? 'dashboard' : view;
    
    const initialExpanded = ["cadastros"];
    // UI-MENU-VENDAS (I.160): Serviços e E-commerce agora ficam dentro de "Vendas"
    if (currentView === 'sales' || currentView === 'invoices' || currentView === 'sales-dashboard'
      || currentView === 'services' || currentView === 'ecommerce') {
      initialExpanded.push('sales');
    }
    if (currentView === 'tasks' || currentView.startsWith('tasks-')) {
      initialExpanded.push('tasks');
    }
    return initialExpanded;
  });
  const { signOut, profile, company, isPermissionsLoading } = useStableAuth();
  const { isMaster, canAccess } = usePermissions();
  const { stats } = useDashboardData();
  const { unreadCount: notificationsUnread } = useNotificationsContext(); // UI-HEADER-FIXO (I.160): fonte única
  const { unreadCount: activitiesUnread } = useTaskActivities();
  const totalInboxUnread = notificationsUnread + activitiesUnread;
  const { hasModule, shouldShowMenuItem } = useModuleAccess();
  const canManageSupplierTrips = canAccess('logistics:supplier_trip_manage');
  const { pendingRateioCount } = useSupplierTrips(
    undefined,
    { enabled: canManageSupplierTrips },
  );


  // Derivar activeView da URL
  const activeView = useMemo(() => {
    const path = location.pathname.split('/').filter(Boolean);
    const view = path[1] || 'home';
    return view === 'home' ? 'dashboard' : view;
  }, [location.pathname]);

  // Memoize subscription status to prevent unnecessary recalculations
  const subscriptionStatus = useMemo(() => {
    if (!company?.subscription_expires_at) return null;
    
    const expiryDate = new Date(company.subscription_expires_at);
    const now = new Date();
    const daysUntilExpiry = Math.ceil((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    
    if (company && new Date(company.subscription_expires_at || '') < new Date()) {
      return { status: 'expired', daysUntilExpiry: 0 };
    } else if (daysUntilExpiry <= 7 && daysUntilExpiry > 0) {
      return { status: 'expiring', daysUntilExpiry };
    }
    
    return null;
  }, [company?.subscription_expires_at]);

  // ✅ PERF: Menu items memoizados para evitar recálculo em cada re-render
  const menuItems = useMemo(() => {
    const baseMenuItems = [
      {
        id: "home",
        label: "Início",
        icon: Home,
        badge: null,
        visible: true  // Todos os usuários veem o dashboard
      },
      {
        id: "crm",
        label: "CRM",
        icon: UserCheck,
        badge: null,
        visible: isMaster || canAccess('sales:is_commissioned') || canAccess('crm:manager_view'),
        submenu: [
          {
            id: "crm-portfolio",
            label: "Carteira de Clientes",
            icon: Users,
            badge: null,
            visible: true
          },
          {
            id: "crm-routine",
            label: "Roteiro de Atendimento",
            icon: Route,
            badge: null,
            visible: shouldShowMenuItem('crm-routine')
          },
          {
            id: "crm-sales-assistant",
            label: "Assistente de Vendas",
            icon: BotMessageSquare,
            badge: null,
            visible: shouldShowMenuItem('crm-sales-assistant')
          },
          {
            id: "crm-customer-map",
            label: "Mapa de Clientes",
            icon: MapPin,
            badge: null,
            visible: true
          },
          {
            id: "crm-my-route",
            label: "Minha Rota",
            icon: Truck,
            badge: null,
            visible: isMaster || canAccess('logistics:driver_app')
          },
          // "Relatório de Atendimentos" removido do menu — acesso via card de Atendimentos (§I.104 D2)
        ].filter(item => item.visible),
      },
      {
        id: "tasks",
        label: "Tarefas",
        icon: CheckSquare,
        badge: null,
        visible: true,
        submenu: [
          {
            id: "tasks",
            label: "Início",
            icon: Home,
            badge: null,
            visible: true
          },
          {
            id: "tasks-my-tasks",
            label: "Minhas Tarefas",
            icon: CheckSquare,
            badge: null,
            visible: true
          },
          {
            id: "tasks-notes",
            label: "Anotações",
            icon: FileText,
            badge: null,
            visible: true
          },
          {
            id: "tasks-inbox",
            label: "Caixa de Entrada",
            icon: Inbox,
            badge: totalInboxUnread > 0 ? totalInboxUnread.toString() : null,
            visible: true
          },
          {
            id: "projects",
            label: "Projetos",
            icon: FolderKanban,
            badge: null,
            visible: true,
          },
        ].filter(item => item.visible)
      },
      {
        id: "sales",
        label: "Vendas",
        icon: ShoppingCart,
        badge: null,
        visible: true,
        // UI-MENU-VENDAS (I.160, Diretor 23/10): ordem Vendas e Orçamentos, PDV, Serviços, Notas Fiscais,
        // (Promoções só quando existir rota), E-commerce, Painel; visibilidade e rotas iguais às de antes.
        submenu: [
          {
            id: "sales",
            label: "Vendas e Orçamentos",
            icon: ShoppingCart,
            badge: null,
            visible: true
          },
          {
            id: "pos-mode",
            label: "PDV",
            icon: Monitor,
            badge: null,
            visible: shouldShowMenuItem('pos-mode')
          },
          {
            id: "services",
            label: "Serviços",
            icon: Wrench,
            badge: null,
            visible: true
          },
          {
            id: "invoices",
            label: "Notas Fiscais",
            icon: FileText,
            badge: null,
            visible: true
          },
          {
            id: "ecommerce",
            label: "E-commerce",
            icon: ShoppingBag,
            badge: null,
            visible: isMaster || canAccess('ecommerce:view'),
          },
          {
            id: "sales-dashboard",
            label: "Painel",
            icon: BarChart3,
            badge: null,
            visible: isMaster || canAccess('sales:view_dashboard')
          },
        ].filter(item => item.visible),
      },
      {
        id: "purchases",
        label: "Compras",
        icon: ShoppingBag,
        badge: null,
        visible: isMaster || canAccess('purchasing:approve')
      },
      {
        id: "logistics",
        label: "Logística",
        icon: Truck,
        badge: null,
        visible: true,
        submenu: [
          {
            id: "picking",
            label: "Triagem",
            icon: CheckSquare,
            badge: null,
            visible: true
          },
          {
            id: "logistics-deliveries",
            label: "Entregas",
            icon: Package,
            badge: null,
            visible: true
          },
          {
            id: "logistics-fuel",
            label: "Abastecimento",
            icon: Fuel,
            badge: (canAccess('logistics:supplier_trip_manage') || isMaster) && pendingRateioCount > 0
              ? pendingRateioCount.toString()
              : null,
            visible: shouldShowMenuItem('logistics-fuel')
          },
          {
            id: "logistics-technicians",
            label: "Técnicos",
            icon: Users,
            badge: null,
            visible: (isMaster || canAccess('logistics:manage')) && shouldShowMenuItem('logistics-technicians')
          },
          {
            id: "logistics-cylinder-analysis",
            label: "Rastreio de Ativos",
            icon: BarChart3,
            badge: null,
            visible: isMaster || canAccess('logistics:view_reports')
          },
        ].filter(item => item.visible),
      },
      {
        // CONSIGNACAO ato 1 (I.180; pacote, decisão 1): Romaneios no básico, com ou sem Locações.
        // ROMANEIO-MENU (I.181, Diretor 03/11): item simples, sem o subitem "Saídas"; o clique abre os cartões
        id: "loans",
        label: "Romaneios",
        icon: ClipboardList,
        badge: stats.activeLoans > 0 ? stats.activeLoans.toString() : null,
        visible: canAccess('logistics:loan_view'),
      },
      {
        id: "financial",
        label: "Financeiro",
        icon: DollarSign,
        badge: null,
        visible: isMaster || canAccess('fin:view_reports'),
        submenu: [
          {
            id: "financial",
            label: "Dashboard",
            icon: BarChart3,
            badge: null,
            visible: isMaster || canAccess('fin:view_reports')
          },
          {
            id: "financial-accounts",
            label: "Contas Financeiras",
            icon: Wallet,
            badge: null,
            visible: isMaster || canAccess('fin:view_reports')
          },
          {
            id: "financial-cashflow",
            label: "Fluxo de Caixa",
            icon: TrendingUp,
            badge: null,
            visible: (isMaster || canAccess('finance:view_cashflow')) && shouldShowMenuItem('financial-cashflow')
          },
          {
            id: "financial-receivables",
            label: "Contas a Receber",
            icon: ArrowUpDown,
            badge: null,
            visible: isMaster || canAccess('fin:receive_pay')
          },
          {
            id: "financial-payables",
            label: "Contas a Pagar",
            icon: ArrowUpDown,
            badge: null,
            visible: isMaster || canAccess('fin:receive_pay')
          },
          {
            id: "financial-collections",
            label: "Cobrança",
            icon: UserCheck,
            badge: null,
            visible: (isMaster || canAccess('fin:view_reports')) && shouldShowMenuItem('financial-collections')
          },
          {
            id: "financial-reconciliation",
            label: "Conciliação Bancária",
            icon: ArrowUpDown,
            badge: null,
            visible: (isMaster || canAccess('fin:bank_sync')) && shouldShowMenuItem('financial-reconciliation')
          },
          {
            id: "dre-management",
            label: "DRE",
            icon: FileText,
            badge: null,
            visible: (isMaster || canAccess('finance:view_dre')) && shouldShowMenuItem('dre-management')
          },
        ].filter(item => item.visible),
      },
      {
        id: "rentals",
        label: "Locações",
        icon: CreditCard,
        badge: null,
        visible: hasModule('MODULE_RENTALS'),
        submenu: [
          {
            id: "rentals",
            label: "Contratos",
            icon: FileText,
            badge: null,
            visible: isMaster || canAccess('rentals:view_contracts')
          },
          {
            id: "rental-assets",
            label: "Gestão de Ativos",
            icon: Boxes,
            badge: null,
            visible: isMaster || canAccess('rentals:view_contracts')
          },
          {
            id: "rental-requests",
            label: "Pedidos de Locação",
            icon: FileText,
            badge: null,
            visible: true
          },
        ].filter(item => item.visible),
      },
      {
        id: "reports",
        label: "Relatórios",
        icon: TrendingUp,
        badge: null,
        visible: isMaster || canAccess('fin:view_reports')
      },
      {
        id: "cadastros",
        label: "Cadastros",
        icon: Users,
        badge: null,
        visible: true,
        submenu: [
          {
            id: "customers",
            label: "Clientes",
            icon: Users,
            badge: null,
            visible: true
          },
          {
            id: "suppliers",
            label: "Fornecedores",
            icon: Building2,
            badge: null,
            visible: isMaster || canAccess('settings:manage_suppliers')
          },
          {
            id: "carriers",
            label: "Transportadoras",
            icon: Truck,
            badge: null,
            visible: isMaster || canAccess('settings:manage_suppliers')
          },
          {
            id: "deposits",
            label: "Depósitos", 
            icon: Building2,
            badge: null,
            visible: isMaster || canAccess('settings:manage_suppliers')
          },
        {
          id: "products",
          label: "Produtos",
          icon: Boxes,
          badge: null,
          visible: true
        },
        {
          id: "stock-management",
          label: "Gestão de Estoque",
          icon: Package,
          badge: null,
          visible: isMaster || canAccess('stock:manual_adjust') || canAccess('stock:conference'),
          submenu: [
            {
              id: "stock-management",
              label: "Gestão de Estoque",
              icon: Package,
              badge: null,
              visible: isMaster || canAccess('stock:manual_adjust')
            },
            {
              id: "stock-entry",
              label: "Entrada de Estoque",
              icon: ArrowDownToLine,
              badge: null,
              visible: isMaster || canAccess('stock:manual_adjust')
            },
            {
              id: "stock-locations",
              label: "Localizações",
              icon: MapPin,
              badge: null,
              visible: isMaster || canAccess('stock:manual_adjust')
            },
            {
              id: "stock-checklist",
              label: "Conferência",
              icon: ClipboardList,
              badge: null,
              visible: isMaster || canAccess('stock:conference')
            },
            {
              // A8-i: menu para quem confere e para o master; ações do balanço só do master (Diretor, 15/10)
              id: "stock-balance",
              label: "Balanço",
              icon: ClipboardList,
              badge: null,
              visible: isMaster || canAccess('stock:conference')
            },
            {
              id: "stock-history",
              label: "Histórico",
              icon: History,
              badge: null,
              visible: isMaster || canAccess('stock:manual_adjust')
            },
            {
              id: "inventory-intelligence",
              label: "Inteligência",
              icon: BarChart3,
              badge: null,
              visible: isMaster || canAccess('stock:manual_adjust')
            },
            {
              id: "stock-transfers",
              label: "Transferências",
              icon: ArrowRightLeft,
              badge: null,
              visible: isMaster || canAccess('org:manage_transfers')
            }
          ].filter(item => item.visible),
        },
      ].filter(item => item.visible),
      },
      {
        id: "production-menu",
        label: "Produção",
        icon: Factory,
        badge: null,
        visible: isMaster && hasModule('MODULE_PRODUCTION'),
        submenu: [
          {
            id: "production-engineering",
            label: "Fichas Técnicas",
            icon: FileText,
            badge: null,
            visible: true
          },
          {
            id: "production-projects",
            label: "Projetos",
            icon: ClipboardList,
            badge: null,
            visible: true
          },
          {
            id: "production-orders",
            label: "Ordens de Produção",
            icon: Factory,
            badge: null,
            visible: true
          },
          {
            id: "production-kanban",
            label: "Kanban de Produção",
            icon: ClipboardList,
            badge: null,
            visible: true
          },
          {
            id: "production-items",
            label: "Itens Produzidos",
            icon: Package,
            badge: null,
            visible: true
          },
          {
            id: "production-qc",
            label: "Controle de Qualidade",
            icon: CheckSquare,
            badge: null,
            visible: true
          },
          {
            id: "production-operators",
            label: "Técnicos Operadores",
            icon: Users,
            badge: null,
            visible: isMaster
          },
          {
            id: "production-work-logs",
            label: "Hora Homem",
            icon: History,
            badge: null,
            visible: true
          },
          {
            id: "production-reports",
            label: "Relatórios",
            icon: BarChart3,
            badge: null,
            visible: isMaster
          },
          {
            id: "production-costs",
            label: "Custos e Análise",
            icon: DollarSign,
            badge: null,
            visible: isMaster
          },
        ].filter(item => item.visible),
      },
      {
        id: "settings",
        label: "Configurações",
        icon: Settings,
        badge: subscriptionStatus ? (
          subscriptionStatus.status === 'expired' ? 'EXPIRADO' : 
          subscriptionStatus.status === 'expiring' ? `${subscriptionStatus.daysUntilExpiry}d` : null
        ) : null,
        visible: isMaster || canAccess('sales:manage_settings') || canAccess('finance:manage_settings')
      },
    ];

    return baseMenuItems.filter(item => item.visible);
  }, [isMaster, canAccess, stats.activeLoans, totalInboxUnread, hasModule, shouldShowMenuItem, profile, subscriptionStatus]);

  if (isPermissionsLoading) {
    return (
      <div className="bg-gradient-card border-r border-border shadow-md flex flex-col h-full w-64 animate-pulse">
        <div className="flex-1 p-3 space-y-2 overflow-hidden">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 px-2 py-2.5">
              <div className="h-4 w-4 bg-muted rounded shrink-0" />
              <div className="h-3.5 bg-muted rounded w-full" style={{ width: `${55 + (i % 4) * 10}%` }} />
            </div>
          ))}
        </div>
        <div className="p-3 border-t border-border">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="h-8 w-8 bg-muted rounded-full shrink-0" />
            <div className="space-y-1.5 flex-1">
              <div className="h-3 bg-muted rounded w-2/3" />
              <div className="h-2.5 bg-muted rounded w-1/2" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "bg-gradient-card border-r border-border shadow-md transition-all duration-300 flex flex-col h-full",
        collapsed ? "w-16" : "w-64"
      )}
    >
      {/* UI-HEADER-FIXO ajuste (I.160, Diretor 23/10 tarde): o bloco da empresa (logo, nome, botão de recolher, seletor
          de empresas) foi para a barra superior; o menu começa direto nos itens */}
      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-2">
        {menuItems.map((item) => {
          const Icon = item.icon;
          const isActive = activeView === item.id;
          const hasSubmenu = item.submenu && item.submenu.length > 0;
          const isExpanded = expandedMenus.includes(item.id);
          const hasActiveSubmenu = hasSubmenu && item.submenu?.some(sub => activeView === sub.id);
          
          if (hasSubmenu) {
            return (
              <Collapsible 
                key={item.id} 
                open={isExpanded} 
                onOpenChange={(open) => {
                  if (open) {
                    setExpandedMenus([...expandedMenus, item.id]);
                  } else {
                    setExpandedMenus(expandedMenus.filter(id => id !== item.id));
                  }
                }}
              >
                <CollapsibleTrigger asChild>
                  <Button
                    variant={hasActiveSubmenu ? "default" : "ghost"}
                    className={cn(
                      "w-full justify-start gap-3 transition-all duration-200",
                      collapsed ? "px-2" : "px-4",
                  hasActiveSubmenu && item.id !== 'cadastros' && "bg-gradient-primary shadow-primary",
                  hasActiveSubmenu && item.id === 'cadastros' && "bg-primary text-primary-foreground shadow-primary",
                  item.id === 'cadastros' && !hasActiveSubmenu && isExpanded && "bg-primary text-primary-foreground hover:bg-primary/90"
                    )}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    {!collapsed && (
                      <>
                        <span className="flex-1 text-left">{item.label}</span>
                        <ChevronDown className={cn(
                          "h-4 w-4 transition-transform duration-200",
                          isExpanded && "transform rotate-180"
                        )} />
                      </>
                    )}
                  </Button>
                </CollapsibleTrigger>
                {!collapsed && (
                  <CollapsibleContent className="space-y-1 ml-6 mt-1">
                    {item.submenu?.map((subItem) => {
                      const SubIcon = subItem.icon;
                      const isSubActive = activeView === subItem.id;
                      
                      return (
                        <Button
                          key={subItem.id}
                          variant={isSubActive ? "default" : "ghost"}
                          size="sm"
                          className={cn(
                            "w-full justify-start gap-2 text-sm",
                            isSubActive && "bg-primary/20 text-primary"
                          )}
                          onClick={() => {
                            // Special case for driver-mode - navigate to /driver
                            if (subItem.id === 'driver-mode') {
                              window.open('/driver', '_blank');
                              return;
                            }
                            // Special case for pos-mode - navigate to /pdv
                            if (subItem.id === 'pos-mode') {
                              window.open('/pdv', '_blank');
                              return;
                            }
                            const routeName = subItem.id === 'dashboard' ? 'home' : subItem.id;
                            navigate(`/dashboard/${routeName}`);
                          }}
                        >
                          <SubIcon className="h-3 w-3 shrink-0" />
                          <span className="flex-1 text-left">{subItem.label}</span>
                          {subItem.badge && (
                            <Badge variant="destructive" className="text-xs px-1.5 py-0.5 min-w-[20px] h-5">
                              {subItem.badge}
                            </Badge>
                          )}
                        </Button>
                      );
                    })}
                  </CollapsibleContent>
                )}
              </Collapsible>
            );
          }
          
          return (
            <Button
              key={item.id}
              variant={isActive ? "default" : "ghost"}
              className={cn(
                "w-full justify-start gap-3 transition-all duration-200",
                collapsed ? "px-2" : "px-4",
                isActive && "bg-gradient-primary shadow-primary"
              )}
              onClick={() => {
                const routeName = item.id === 'dashboard' ? 'home' : item.id;
                navigate(`/dashboard/${routeName}`);
              }}
            >
              <Icon className="h-4 w-4 shrink-0" />
              {!collapsed && (
                <>
                  <span className="flex-1 text-left">{item.label}</span>
                  {item.badge && (
                    <Badge 
                      variant={
                        item.id === 'settings' && subscriptionStatus?.status === 'expired' ? 'destructive' :
                        item.id === 'settings' && subscriptionStatus?.status === 'expiring' ? 'secondary' :
                        'destructive'
                      }
                      className={cn(
                        "text-xs px-1.5 py-0.5 min-w-[20px] h-5",
                        item.id === 'settings' && subscriptionStatus?.status === 'expiring' && "bg-orange-500 text-white hover:bg-orange-600"
                      )}
                    >
                      {item.badge}
                    </Badge>
                  )}
                </>
              )}
            </Button>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-border space-y-4">
        <Button
          variant="ghost"
          size={collapsed ? "icon" : "sm"}
          className="w-full justify-start text-muted-foreground hover:text-foreground hover:bg-accent/50"
          onClick={signOut}
        >
          <LogOut className="h-4 w-4" />
          {!collapsed && <span className="ml-2">Sair</span>}
        </Button>
        
        {!collapsed ? (
          <div className="text-center">
            <p className="text-xs text-muted-foreground">
              © 2024 GOnX
            </p>
            <p className="text-xs text-muted-foreground">
              v1.0.0
            </p>
          </div>
        ) : (
          <div className="h-8" />
        )}
      </div>
    </div>
  );
};

// Memoize the sidebar component to prevent unnecessary re-renders
export const Sidebar = memo(SidebarComponent);